import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { number as validateCardNumber } from "card-validator";
import { AvailablePaymentMethod, CartResponse } from "@/networking/carts.api";
import {
  PaymentBehavior,
  SPECIAL_PAYMENT_METHODS,
} from "@/networking/payments.api";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatExpirationDate(date: string) {
  // Remove all non-numeric characters except for "/"
  date = date.replace(/[^\d\/]/g, "");
  // Limit the length to 7 characters to accommodate MM/YYYY format
  date = date.slice(0, 7);
  // Automatically add "/" between the first 2 and the remaining characters
  if (date.length > 2 && date[2] !== "/") {
    date = date.slice(0, 2) + "/" + date.slice(2);
  }
  return date;
}

export function formatPhoneNumber(phone: string) {
  return phone.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
}

export function formatCardNumber(cardNumber: string) {
  cardNumber = cardNumber.replace(/\D/g, "");

  const cardValidation = validateCardNumber(cardNumber);
  const cardType = cardValidation.card?.type;
  const cardIsValid = cardValidation.isValid;
  let formattedCardNumber = "";
  if (cardValidation.card) {
    const { lengths, gaps } = cardValidation.card;
    const maxLength = Math.max(...lengths);
    cardNumber = cardNumber.slice(0, maxLength);

    const parts: string[] = [];
    let currentPosition = 0;
    gaps.forEach((gap) => {
      parts.push(cardNumber.slice(currentPosition, gap));
      currentPosition = gap;
    });
    parts.push(cardNumber.slice(currentPosition));
    formattedCardNumber = parts.join(" ").trim();
  } else {
    formattedCardNumber = cardNumber.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
  }

  return {
    formattedCardNumber,
    cardType,
    cardIsValid,
  };
}

export function formatCVC(cvc: string) {
  return cvc.replace(/\D/g, "").slice(0, 4);
}

export function formatSocialSecurityNumber(ssn: string) {
  ssn = ssn.replace(/\D/g, "").slice(0, 9);
  return ssn.replace(/(\d{3})(\d{2})(\d{4})/, "$1-$2-$3");
}

export function validateExpirationDate(date: string) {
  const errors: string[] = [];

  if (date.length < 5) {
    errors.push("please_enter_valid_expiration_date");
    return errors;
  }

  if (date.length === 5 && date.includes("/")) {
    const parts = date.split("/");
    const month = parts[0];
    const year = parts[1];

    if (month && year) {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear() % 100;
      const currentMonth = currentDate.getMonth() + 1;

      const monthNum = parseInt(month, 10);
      const yearNum = parseInt(year, 10);

      if (isNaN(monthNum) || isNaN(yearNum)) {
        errors.push("invalid_date_format");
      } else if (monthNum < 1 || monthNum > 12) {
        errors.push("invalid_month");
      } else if (
        yearNum < currentYear ||
        (yearNum === currentYear && monthNum < currentMonth)
      ) {
        errors.push("card_has_expired");
      }
    }
  }

  return errors;
}

export function validateCVC(cvc: string, cardType: string) {
  const errors: string[] = [];
  const expectedLength = cardType === "amex" ? 4 : 3;

  if (cvc.length < expectedLength) {
    errors.push(expectedLength === 4 ? "cvc_must_be_4_digits" : "cvc_must_be_3_digits");
  }

  return errors;
}

export function validateName(name: string) {
  const errors: string[] = [];

  if (name.trim().length < 2) {
    errors.push("name_must_be_at_least_2_characters");
  }

  return errors;
}

export function validateZipCode(zip: string) {
  const errors: string[] = [];

  if (zip.trim().length < 3) {
    errors.push("please_enter_valid_zip_code");
  }

  return errors;
}

export function formatName(name: string) {
  // Remove numbers and special characters, allow only letters, spaces, hyphens, and apostrophes
  return name.replace(/[^a-zA-Z\s'-]/g, "");
}

export function formatZipCode(zip: string) {
  // Allow only alphanumeric characters for zip codes (supports both US and international formats)
  return zip.replace(/[^a-zA-Z0-9\s-]/g, "").slice(0, 10);
}

export function formatInputValue(value: string, type: string) {
  switch (type) {
    case "Mobile phone number":
      value = formatPhoneNumber(value);
      break;
    case "MM / YY":
      value = formatExpirationDate(value);
      break;
    case "Card Number":
      value = formatCardNumber(value).formattedCardNumber;
      break;
    case "CVC":
      value = formatCVC(value);
      break;
    case "Social Security Number":
      value = formatSocialSecurityNumber(value);
      break;
    case "Name":
      value = formatName(value);
      break;
    case "Zip Code":
      value = formatZipCode(value);
      break;
  }
  return value;
}

export function formatPrice(price: string) {
  return price.replace(/\s*\(.*?\)\s*/g, "");
}

export type DecodedJWT = {
  user_company_id: number;
  full_name: string;
  email: string;
  exp: number;
  id: number;
};

export function decodeJWT(token?: string | null): DecodedJWT | null {
  if (!token) return null;
  try {
    const [, payloadBase64] = token.split(".");

    if (!payloadBase64) {
      throw new Error("Invalid token structure");
    }

    const payload = atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/"));

    const parsedPayload = JSON.parse(payload);

    const expirationTime = parsedPayload.exp as number;
    // TODO: Remove this once we want to check real expiration time. this will let us test using this JWT: eyJhbGciOiJIUzI1NiJ9.eyJ1c2VyX2NvbXBhbnlfaWQiOjc5NjA4OCwiZnVsbF9uYW1lIjoiU2V0aCBXIiwiZW1haWwiOiJzZXRoZ29yZG9ud0BnbWFpbC5jb20iLCJleHAiOjE3MzYyMDAyOTd9.CkofkoZwCOZ0jZr03XecAsTj98urFteN_XAshWXJFTg
    if (expirationTime && Date.now() >= expirationTime * 1000 && false) {
      console.warn("Token has expired");
      return null;
    }

    return parsedPayload;
  } catch (error) {
    console.error("Failed to decode JWT:", error);
    return null;
  }
}

export function formatDate(dateString: string, isCancelled: boolean = false) {
  if (isCancelled) return "Cancelled";
  if (!dateString) return "Paused";

  const parts = dateString.split("-");
  if (parts.length !== 3) return "Invalid date";

  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return "Invalid date";
  const date = new Date(year, month - 1, day); // month is 0-indexed

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNum = date.getDate();
  const monthName = monthNames[date.getMonth()];
  const yearNum = date.getFullYear();

  const ordinalSuffix = (day: number) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${monthName} ${dayNum}${ordinalSuffix(dayNum)}, ${yearNum}`;
}

type TranslationFn = (key: string) => string;

export function formatDateTime(dateString: string, t: TranslationFn): string {
  const date = new Date(dateString);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);

  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  const hour12 = hours % 12 || 12;

  return `${month}/${day}/${year} ${t("at_time")} ${hour12}:${minutes} ${ampm}`;
}

export function formatSSN(ssn: string) {
  const digitsOnly = ssn.replace(/\D/g, "");

  const limitedDigits = digitsOnly.slice(0, 9);

  if (limitedDigits.length <= 3) {
    return limitedDigits;
  } else if (limitedDigits.length <= 5) {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(3)}`;
  } else {
    return `${limitedDigits.slice(0, 3)}-${limitedDigits.slice(
      3,
      5
    )}-${limitedDigits.slice(5)}`;
  }
}

type Item = {
  allow_subscription: boolean;
  subscription_interval: number | null;
  subscription_price_in_currency: string | null;
};

export function getSubscriptionPrice(item: Item) {
  if (!item.allow_subscription) return null;
  const intervalText =
    item.subscription_interval === 1
      ? "month"
      : `${item.subscription_interval} mo.`;
  return `${item.subscription_price_in_currency}/${intervalText}`;
}

export const usStates = [
  { name: "Alabama", isoCode: "AL" },
  { name: "Alaska", isoCode: "AK" },
  { name: "Arizona", isoCode: "AZ" },
  { name: "Arkansas", isoCode: "AR" },
  { name: "California", isoCode: "CA" },
  { name: "Colorado", isoCode: "CO" },
  { name: "Connecticut", isoCode: "CT" },
  { name: "Delaware", isoCode: "DE" },
  { name: "Florida", isoCode: "FL" },
  { name: "Georgia", isoCode: "GA" },
  { name: "Hawaii", isoCode: "HI" },
  { name: "Idaho", isoCode: "ID" },
  { name: "Illinois", isoCode: "IL" },
  { name: "Indiana", isoCode: "IN" },
  { name: "Iowa", isoCode: "IA" },
  { name: "Kansas", isoCode: "KS" },
  { name: "Kentucky", isoCode: "KY" },
  { name: "Louisiana", isoCode: "LA" },
  { name: "Maine", isoCode: "ME" },
  { name: "Maryland", isoCode: "MD" },
  { name: "Massachusetts", isoCode: "MA" },
  { name: "Michigan", isoCode: "MI" },
  { name: "Minnesota", isoCode: "MN" },
  { name: "Mississippi", isoCode: "MS" },
  { name: "Missouri", isoCode: "MO" },
  { name: "Montana", isoCode: "MT" },
  { name: "Nebraska", isoCode: "NE" },
  { name: "Nevada", isoCode: "NV" },
  { name: "New Hampshire", isoCode: "NH" },
  { name: "New Jersey", isoCode: "NJ" },
  { name: "New Mexico", isoCode: "NM" },
  { name: "New York", isoCode: "NY" },
  { name: "North Carolina", isoCode: "NC" },
  { name: "North Dakota", isoCode: "ND" },
  { name: "Ohio", isoCode: "OH" },
  { name: "Oklahoma", isoCode: "OK" },
  { name: "Oregon", isoCode: "OR" },
  { name: "Pennsylvania", isoCode: "PA" },
  { name: "Rhode Island", isoCode: "RI" },
  { name: "South Carolina", isoCode: "SC" },
  { name: "South Dakota", isoCode: "SD" },
  { name: "Tennessee", isoCode: "TN" },
  { name: "Texas", isoCode: "TX" },
  { name: "Utah", isoCode: "UT" },
  { name: "Vermont", isoCode: "VT" },
  { name: "Virginia", isoCode: "VA" },
  { name: "Washington", isoCode: "WA" },
  { name: "West Virginia", isoCode: "WV" },
  { name: "Wisconsin", isoCode: "WI" },
  { name: "Wyoming", isoCode: "WY" },
];

export function formatExpirationDateForSpreedly(expiration_date: string) {
  const [month, yearStr] = expiration_date?.split("/") || [];

  // Handle year conversion
  let fullYear = yearStr || "";
  if (yearStr?.length === 2) {
    // For 2-digit years, assume 20xx for years 00-99
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    const twoDigitYear = parseInt(yearStr, 10);
    fullYear = (currentCentury + twoDigitYear).toString();
  }

  return { month, year: fullYear };
}

export const convertPriceToPositiveInteger = (price: string): number => {
  if (!price) return 0;
  const floatPrice = parseFloat(price.replace(/[^0-9.-]+/g, ""));
  return Math.round(floatPrice * 100);
};

export const formatSubscriptionFrequency = (frequency: string) => {
  if (frequency === "day") return "Daily";
  if (frequency === "week") return "Weekly";
  if (frequency === "month") return "Monthly";
  if (frequency === "year") return "Yearly";
  return frequency;
};

export const capitalizeFirstLetter = (value: string) => {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

export const calculateResumeDate = (
  billingInterval: number,
  billingIntervalUnit: string,
  orderCount: number,
  fromDate?: string | Date | null
): Date => {
  let startDate: Date;

  if (fromDate) {
    if (typeof fromDate === "string") {
      startDate = new Date(
        fromDate + (fromDate.includes("T") ? "" : "T12:00:00.000Z")
      );
    } else {
      startDate = new Date(
        Date.UTC(
          fromDate.getUTCFullYear(),
          fromDate.getUTCMonth(),
          fromDate.getUTCDate(),
          12,
          0,
          0,
          0
        )
      );
    }
  } else {
    const now = new Date();
    startDate = new Date(
      Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        12,
        0,
        0,
        0
      )
    );
  }

  const baseDate = new Date(startDate.getTime());
  const totalIntervals = billingInterval * orderCount;

  switch (billingIntervalUnit.toLowerCase()) {
    case "day":
      baseDate.setUTCDate(baseDate.getUTCDate() + totalIntervals);
      break;
    case "week":
      baseDate.setUTCDate(baseDate.getUTCDate() + totalIntervals * 7);
      break;
    case "month":
      const currentMonth = baseDate.getUTCMonth();
      const currentDay = baseDate.getUTCDate();
      baseDate.setUTCMonth(currentMonth + totalIntervals);

      if (baseDate.getUTCDate() !== currentDay) {
        baseDate.setUTCDate(0);
      }
      break;
    case "year":
      baseDate.setUTCFullYear(baseDate.getUTCFullYear() + totalIntervals);
      break;
    default:
      const defaultMonth = baseDate.getUTCMonth();
      const defaultDay = baseDate.getUTCDate();
      baseDate.setUTCMonth(defaultMonth + totalIntervals);

      if (baseDate.getUTCDate() !== defaultDay) {
        baseDate.setUTCDate(0);
      }
  }

  return baseDate;
};

export function calculateNextBillDate(
  billingInterval: number = 1,
  billingIntervalUnit: string = "month"
): string {
  const now = new Date();
  const nextBillDate = new Date(now);

  switch (billingIntervalUnit.toLowerCase()) {
    case "day":
      nextBillDate.setDate(now.getDate() + billingInterval);
      break;
    case "week":
      nextBillDate.setDate(now.getDate() + billingInterval * 7);
      break;
    case "month":
      nextBillDate.setMonth(now.getMonth() + billingInterval);
      break;
    case "year":
      nextBillDate.setFullYear(now.getFullYear() + billingInterval);
      break;
    default:
      // Default to monthly if unknown unit
      nextBillDate.setMonth(now.getMonth() + billingInterval);
  }

  // Format as YYYY-MM-DD
  return nextBillDate.toISOString().split("T")[0] || "";
}

export function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

// Calculate total price by multiplying unit price by quantity
export function calculateTotalPrice(unitPrice: number, qty: number): string {
  return (unitPrice * qty).toFixed(2);
}

export const getNextBillDisplay = <
  T extends { status?: string; next_bill_date?: string | null }
>(
  subscription: T | null | undefined
) => {
  if (subscription?.status === "cancelled") return "Cancelled";
  if (subscription?.next_bill_date)
    return formatDate(subscription.next_bill_date);
  if (subscription?.status === "paused") return "Paused";
  return "N/A";
};

// Calculate subscription price by removing the discount amount
export function calculateSubscriptionPrice(
  totalPrice: number,
  subscribeAndSave: string | null | undefined,
  quantity: number
): number {
  if (!subscribeAndSave) return totalPrice;

  const unitDiscountAmount = parseFloat(subscribeAndSave);
  if (isNaN(unitDiscountAmount)) return totalPrice;

  const subscriptionAmount = totalPrice - unitDiscountAmount * quantity;
  return Number(subscriptionAmount.toFixed(2));
}

// Calculate total savings amount
export function calculateTotalSavings(
  subscribeAndSave: string | null | undefined,
  quantity: number
): number {
  if (!subscribeAndSave) return 0;

  const unitDiscountAmount = parseFloat(subscribeAndSave);
  if (isNaN(unitDiscountAmount)) return 0;

  return Number((unitDiscountAmount * quantity).toFixed(2));
}

// Recurring items calculation functions
export function getRecurringItems(
  cart: CartResponse["cart"] | null | undefined
) {
  return (
    cart?.recurring?.filter((recurring) => recurring.subtotal !== "0.0") || []
  );
}

export function calculateTotalRecurringAmount(
  recurringItems: NonNullable<CartResponse["cart"]["recurring"]>
) {
  return recurringItems.reduce((total, recurring) => {
    const amount = parseFloat(recurring.subtotal);
    return total + (isNaN(amount) ? 0 : amount);
  }, 0);
}

export function getCurrencySymbol(
  recurringItems: NonNullable<CartResponse["cart"]["recurring"]>
) {
  return (
    recurringItems[0]?.subtotal_for_display?.match(/^[^\d\s]+/)?.[0] || "$"
  );
}

export function getEarliestStartDate(
  recurringItems: NonNullable<CartResponse["cart"]["recurring"]>
) {
  return recurringItems
    .filter((item) => item.start_date)
    .map((item) => new Date(item.start_date!))
    .sort((a, b) => a.getTime() - b.getTime())[0];
}

// payment-utils
export function getPaymentBehavior(paymentType: string): PaymentBehavior {
  // Check if it's a special payment method
  if (paymentType in SPECIAL_PAYMENT_METHODS) {
    return SPECIAL_PAYMENT_METHODS[
      paymentType as keyof typeof SPECIAL_PAYMENT_METHODS
    ];
  }

  // Default behavior for ANY unknown payment method is redirect
  return PaymentBehavior.REDIRECT_BASED;
}

export function getPaymentMethodDisplayName(
  availableMethod: AvailablePaymentMethod
): string {
  return (
    availableMethod.name ||
    availableMethod.type
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase())
  );
}

export function getPaymentMethodButtonText(
  paymentType: string,
  methodName?: string
): string {
  const behavior = getPaymentBehavior(paymentType);

  switch (behavior) {
    case PaymentBehavior.CREDIT_CARD:
      return "Pay Now";
    case PaymentBehavior.DIRECT_CHECKOUT:
      return "Pay Now";
    default:
      return `Pay with ${
        methodName ||
        paymentType.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      }`;
  }
}

export function getPaymentMethodRedirectMessage(
  paymentType: string,
  paymentName: string
): string | null {
  const behavior = getPaymentBehavior(paymentType);

  switch (behavior) {
    case PaymentBehavior.MODAL_BASED:
      if (paymentType === "affirm") {
        return "After clicking 'Pay with Affirm', complete the Affirm flow.";
      }
      if (paymentType === "klarna") {
        return "After clicking 'Pay with Klarna', you will be prompted to complete your purchase with Klarna.";
      }
      return `After clicking 'Pay with ${paymentName}', you will be prompted to complete your purchase.`;

    case PaymentBehavior.REDIRECT_BASED:
      return `After clicking 'Pay with ${paymentName}', you will be redirected to complete your payment with ${paymentName}.`;

    case PaymentBehavior.CUSTOM_COMPONENT:
      if (paymentType === "paypal") {
        return "After clicking 'PayPal', you will finish the payment on PayPal.";
      }
      if (paymentType === "bread") {
        return "After clicking 'Pay now', you will be redirected to Bread Pay to complete financing.";
      }
      return `After clicking 'Pay with ${paymentName}', you will be redirected to complete your payment.`;

    case PaymentBehavior.DIRECT_CHECKOUT:
    case PaymentBehavior.CREDIT_CARD:
    default:
      return null;
  }
}

export const languages = [
  {
    id: 51848956,
    iso: "es",
    name: "Español (Spanish)",
  },
  {
    id: 980190962,
    iso: "en",
    name: "English",
  },
  {
    id: 1018350796,
    iso: "pt",
    name: "Português (Portuguese)",
  },
  {
    id: 113629430,
    iso: "pl",
    name: "Polski (Polish)",
  },
  {
    id: 1018350797,
    iso: "he",
    name: "עברית (Hebrew)",
  },
  {
    id: 1018350798,
    iso: "nl",
    name: "Nederlands (Dutch)",
  },
  {
    id: 1018350799,
    iso: "de",
    name: "Deutsch (German)",
  },
  {
    id: 1018350800,
    iso: "hu",
    name: "Magyar (Hungarian)",
  },
  {
    id: 1018350801,
    iso: "id",
    name: "Bahasa Indonesia (Indonesian)",
  },
  {
    id: 1018350802,
    iso: "it",
    name: "Italiano (Italian)",
  },
  {
    id: 298486374,
    iso: "fr",
    name: "Français (French)",
  },
  {
    id: 281110143,
    iso: "ru",
    name: "Русский (Russian)",
  },
  {
    id: 1018350795,
    iso: "ja",
    name: "日本語 (Japanese)",
  },
  {
    id: 1018350803,
    iso: "zh",
    name: "中文 (Chinese)",
  },
  {
    id: 1018350804,
    iso: "tl",
    name: "Tagalog",
  },
  {
    id: 1018350805,
    iso: "th",
    name: "ไทย (Thai)",
  },
  {
    id: 1018350806,
    iso: "ko",
    name: "한국어 (Korean)",
  },
  {
    id: 1018350807,
    iso: "el",
    name: "Ελληνικά (Greek)",
  },
  {
    id: 1018350808,
    iso: "ro",
    name: "Română (Romanian)",
  },
  {
    id: 1018350809,
    iso: "tr",
    name: "Türkçe (Turkish)",
  },
];
